'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import {
  FolderOpen,
  Terminal,
  Plus,
  X,
  Copy,
  Eye,
  EyeOff,
  Pencil,
  Sparkles,
  Settings,
  MoreVertical,
  Undo2,
  Redo2,
  Columns3,
  BookOpen,
  Github,
  Table2,
  Workflow,
  Check,
  LoaderCircle,
  AlertCircle,
} from 'lucide-react';
import { EquaFlowCanvas } from '@/components/equaflow/canvas';
import { ProjectsPanel } from '@/components/equaflow/projects-panel';
import { ImportDSLDialog } from '@/components/equaflow/import-dsl-dialog';
import { AIBuilderDialog } from '@/components/equaflow/ai-builder-dialog';
import { SettingsPanel } from '@/components/equaflow/settings-panel';
import { SpreadsheetView } from '@/components/equaflow/spreadsheet-view';
import { ModelOverview } from '@/components/equaflow/model-overview';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Toaster } from '@/components/ui/sonner';
import { useEquaFlowStore } from '@/lib/store';
import { getCurrentProjectId } from '@/lib/project-storage';
import type { ProjectOperationContext } from '@/lib/types';
import { toast } from 'sonner';

export default function Home() {
  const currentProjectName = useEquaFlowStore((s) => s.currentProjectName);
  const currentProjectId = useEquaFlowStore((s) => s.currentProjectId);
  const persistenceStatus = useEquaFlowStore((s) => s.persistenceStatus);
  const persistenceError = useEquaFlowStore((s) => s.persistenceError);
  const entities = useEquaFlowStore((s) => s.entities);
  const entityCount = useEquaFlowStore((s) => Object.keys(s.entities).length);
  const scenarios = useEquaFlowStore((s) => s.scenarios);
  const activeScenario = useEquaFlowStore((s) => s.activeScenario);
  const setActiveScenario = useEquaFlowStore((s) => s.setActiveScenario);
  const addScenario = useEquaFlowStore((s) => s.addScenario);
  const removeScenario = useEquaFlowStore((s) => s.removeScenario);
  const toggleComparison = useEquaFlowStore((s) => s.toggleComparison);
  const comparisonMode = useEquaFlowStore((s) => s.comparisonMode);
  const timeEnabled = useEquaFlowStore((s) => s.timeEnabled);
  const timeHorizon = useEquaFlowStore((s) => s.timeHorizon);
  const toggleTimeSeries = useEquaFlowStore((s) => s.toggleTimeSeries);
  const setTimeHorizon = useEquaFlowStore((s) => s.setTimeHorizon);
  const solveStatus = useEquaFlowStore((s) => s.solveStatus);
  const showSolution = useEquaFlowStore((s) => s.showSolution);
  const toggleShowSolution = useEquaFlowStore((s) => s.toggleShowSolution);
  const saveAsCopy = useEquaFlowStore((s) => s.saveAsCopy);
  const canUndo = useEquaFlowStore((s) => s.canUndo);
  const canRedo = useEquaFlowStore((s) => s.canRedo);
  const projectRevision = useEquaFlowStore((s) => s.projectRevision);
  const selectedKpiFieldId = useEquaFlowStore((s) => s.selectedKpiFieldId);
  const selectKpi = useEquaFlowStore((s) => s.selectKpi);
  const setComparisonMode = useEquaFlowStore((s) => s.setComparisonMode);
  const undo = useEquaFlowStore((s) => s.undo);
  const redo = useEquaFlowStore((s) => s.redo);

  const [projectsOpen, setProjectsOpen] = useState(false);
  const [dslDialogOpen, setDslDialogOpen] = useState(false);
  const [aiBuilderOpen, setAiBuilderOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState('');
  const [addScenarioOpen, setAddScenarioOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [saveAsCopyOpen, setSaveAsCopyOpen] = useState(false);
  const [copyName, setCopyName] = useState('');
  const [workspaceView, setWorkspaceView] = useState<'spreadsheet' | 'visual'>('visual');
  const initialModelLoaded = useRef(false);
  const toggleWorkspaceView = useCallback(() => {
    setWorkspaceView((view) => view === 'spreadsheet' ? 'visual' : 'spreadsheet');
  }, []);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTyping = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.tagName === 'SELECT' || target?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        const store = useEquaFlowStore.getState();
        store.autoSave();
        if (useEquaFlowStore.getState().persistenceStatus === 'error') {
          toast.error(useEquaFlowStore.getState().persistenceError || 'Could not save on this device');
        } else {
          toast.success('Saved on this device');
        }
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'v' && !isTyping) {
        e.preventDefault();
        toggleWorkspaceView();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !isTyping) {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'y' && !isTyping) {
        e.preventDefault();
        redo();
      }
      // Ctrl+D: duplicate selected entity
      if ((e.metaKey || e.ctrlKey) && e.key === 'd' && !isTyping) {
        e.preventDefault();
        const store = useEquaFlowStore.getState();
        const selectedEntityId = store.selectedField?.entityId;
        if (selectedEntityId) {
          store.duplicateEntity(selectedEntityId);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo, toggleWorkspaceView]);

  // Auto-load last project on startup
  useEffect(() => {
    const store = useEquaFlowStore.getState();
    if (!initialModelLoaded.current && !store.currentProjectId && Object.keys(store.entities).length === 0) {
      initialModelLoaded.current = true;
      const lastProjectId = getCurrentProjectId();
      const restored = lastProjectId ? store.loadFromBrowser(lastProjectId) : false;
      if (!restored && Object.keys(useEquaFlowStore.getState().entities).length === 0) {
        // This compact example includes formulas, time-series and editable
        // charts, so a first visit opens on a useful model rather than a blank canvas.
        store.loadDemo('saas');
      }
    }

    // Force-save on page unload / tab close (synchronous)
    const handleBeforeUnload = () => {
      const s = useEquaFlowStore.getState();
      if (s.currentProjectId || Object.keys(s.entities).length > 0) {
        s.autoSave();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleAddScenario = useCallback(() => {
    const name = newScenarioName.trim();
    if (name && name !== 'Base') {
      addScenario(name);
      setNewScenarioName('');
      setAddScenarioOpen(false);
    }
  }, [newScenarioName, addScenario]);

  const handleSaveAsCopy = useCallback(() => {
    const name = copyName.trim() || `${currentProjectName} (copy)`;
    const newId = saveAsCopy(name);
    if (newId) {
      toast.success(`Saved as "${name}"`);
      setSaveAsCopyOpen(false);
      setCopyName('');
    }
  }, [copyName, currentProjectName, saveAsCopy]);

  const requestSolveAtRevision = useCallback(async ({ expectedRevision, context }: { expectedRevision: number; context: ProjectOperationContext }) => {
    const state = useEquaFlowStore.getState();
    const scenario = state.scenarios.find((item) => item.id === context.scenarioId);
    if (state.projectRevision !== expectedRevision || state.currentProjectId !== context.projectId || !scenario || scenario.name !== state.activeScenario) throw new Error('The project or scenario changed before solving.');
    window.dispatchEvent(new CustomEvent('equaflow:refresh-model'));
  }, []);

  const requestCompareAtRevision = useCallback(async ({ expectedRevision, context, scenarioNames }: { expectedRevision: number; context: ProjectOperationContext; scenarioNames: string[] }) => {
    const state = useEquaFlowStore.getState();
    const active = state.scenarios.find((scenario) => scenario.id === context.scenarioId);
    if (state.projectRevision !== expectedRevision || state.currentProjectId !== context.projectId || !active || active.name !== state.activeScenario) throw new Error('The project or scenario changed before comparison.');
    const known = new Set(state.scenarios.map((scenario) => scenario.name));
    if (scenarioNames.length < 2 || scenarioNames.some((name) => !known.has(name))) throw new Error('The requested scenarios are no longer available.');
    state.setComparisonScenarioNames(scenarioNames);
    setComparisonMode(true);
  }, [setComparisonMode]);



  const scenarioColors: Record<string, string> = {
    'Base': 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-700',
    'High Cost': 'bg-red-100 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-700',
    'Low Subscribers': 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700',
    'High Subscribers': 'bg-green-100 text-green-700 border-green-300 dark:bg-green-950/40 dark:text-green-300 dark:border-green-700',
  };

  const getScenarioStyle = (name: string, isActive: boolean) => {
    const baseStyle = scenarioColors[name] || 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600';
    if (isActive) {
      return `${baseStyle} ring-2 ring-offset-1 ring-primary/50 font-semibold`;
    }
    return baseStyle;
  };

  const startEditingName = () => {
    setNameInput(currentProjectName);
    setEditingName(true);
  };

  const commitNameEdit = () => {
    const trimmed = nameInput.trim();
    if (trimmed && trimmed !== currentProjectName) {
      useEquaFlowStore.getState().setCurrentProject(currentProjectId, trimmed);
    }
    setEditingName(false);
  };

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden">
      {/* ── Header: Multi-panel layout ── */}
      <header className="shrink-0 border-b border-border bg-card/80 backdrop-blur-sm z-10">
        {/* Row 1: Logo + Project name + Save/Load/Export */}
        <div className="flex flex-wrap items-center gap-2 px-3 py-1.5 sm:gap-3 sm:px-4">
          {/* Logo */}
          <div className="flex items-center gap-2 shrink-0">
            <img src="/logo.svg" alt="EquaFlow" className="w-7 h-7 rounded-lg" />
            <h1 className="text-base font-semibold tracking-tight">
              EquaFlow
            </h1>
          </div>

          <div className="hidden h-5 w-px bg-border sm:block" />

          {/* Projects button — prominent, always visible */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setProjectsOpen(true)}
            title="Открыть проекты и примеры моделей"
            className="h-7 text-xs bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 text-blue-700 hover:from-blue-100 hover:to-indigo-100 dark:from-blue-950/30 dark:to-indigo-950/30 dark:border-blue-800 dark:text-blue-400 dark:hover:from-blue-950/50 dark:hover:to-indigo-950/50"
          >
            <FolderOpen className="h-3 w-3 mr-1" />
            Проекты
          </Button>

          <div className="hidden h-5 w-px bg-border sm:block" />

          {/* AI Build button - prominent */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAiBuilderOpen(true)}
            title="Объяснить, изменить или сравнить модель с ИИ"
            className="h-7 text-xs bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200 text-violet-700 hover:from-violet-100 hover:to-purple-100 dark:from-violet-950/30 dark:to-purple-950/30 dark:border-violet-800 dark:text-violet-400 dark:hover:from-violet-950/50 dark:hover:to-purple-950/50"
          >
            <Sparkles className="h-3 w-3 mr-1" />
            Помощник модели
          </Button>

          <div className="order-1 flex h-7 shrink-0 items-center rounded-md border border-emerald-200 bg-emerald-50/70 p-0.5 dark:border-emerald-800 dark:bg-emerald-950/20 sm:order-none" role="group" aria-label="Режим отображения модели">
            <button
              type="button"
              onClick={() => setWorkspaceView('spreadsheet')}
              aria-pressed={workspaceView === 'spreadsheet'}
              title="Таблица модели (Ctrl/Cmd+Shift+V — переключить режим)"
              className={`inline-flex h-full items-center rounded px-2 text-xs font-medium transition-colors ${workspaceView === 'spreadsheet' ? 'bg-card text-emerald-800 shadow-sm dark:text-emerald-200' : 'text-emerald-700/70 hover:text-emerald-800 dark:text-emerald-300/70 dark:hover:text-emerald-200'}`}
            >
              <Table2 className="mr-1 h-3 w-3" /> Таблица
            </button>
            <button
              type="button"
              onClick={() => setWorkspaceView('visual')}
              aria-pressed={workspaceView === 'visual'}
              title="Визуальная схема (Ctrl/Cmd+Shift+V — переключить режим)"
              className={`inline-flex h-full items-center rounded px-2 text-xs font-medium transition-colors ${workspaceView === 'visual' ? 'bg-card text-emerald-800 shadow-sm dark:text-emerald-200' : 'text-emerald-700/70 hover:text-emerald-800 dark:text-emerald-300/70 dark:hover:text-emerald-200'}`}
            >
              <Workflow className="mr-1 h-3 w-3" /> Схема
            </button>
          </div>

          <div className="hidden h-5 w-px bg-border sm:block" />

          {/* Project name (editable) */}
          <div className="hidden min-w-0 items-center gap-1.5 sm:flex">
            {editingName ? (
              <div className="flex items-center gap-1">
                <Input
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitNameEdit();
                    if (e.key === 'Escape') setEditingName(false);
                  }}
                  onBlur={commitNameEdit}
                  className="h-6 text-xs w-40 border-0 bg-transparent p-0 shadow-none focus-visible:ring-1 focus-visible:ring-primary"
                  autoFocus
                />
              </div>
            ) : (
              <button
                onClick={startEditingName}
                className="flex items-center gap-1 text-sm font-medium hover:opacity-80 transition-opacity"
                title="Click to rename project"
              >
                {currentProjectId ? (
                  <span className="text-emerald-600 dark:text-emerald-400 truncate max-w-[200px]">
                    {currentProjectName}
                  </span>
                ) : (
                  <span className="text-muted-foreground truncate max-w-[200px]">
                    {currentProjectName}
                  </span>
                )}
                <Pencil className="h-2.5 w-2.5 text-muted-foreground/50" />
              </button>
            )}
            <span
              className={`inline-flex items-center gap-1 text-[10px] whitespace-nowrap ${persistenceStatus === 'error' ? 'text-red-600 dark:text-red-400' : persistenceStatus === 'unsaved' ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}
              title={persistenceError || (persistenceStatus === 'saved' ? 'Saved on this device' : persistenceStatus === 'saving' ? 'Saving on this device' : 'Unsaved changes')}
              role="status"
            >
              {persistenceStatus === 'saving' ? <LoaderCircle className="h-3 w-3 animate-spin" /> : persistenceStatus === 'error' ? <AlertCircle className="h-3 w-3" /> : <Check className="h-3 w-3" />}
              {persistenceStatus === 'saving' ? 'Сохраняем…' : persistenceStatus === 'error' ? 'Ошибка сохранения' : persistenceStatus === 'unsaved' ? 'Не сохранено' : 'На устройстве'}
            </span>
          </div>

          <div className="hidden flex-1 sm:block" />

          {/* Undo/Redo + Menu */}
          <div className="flex items-center gap-1">
            {/* Undo */}
            <Button
              variant="ghost"
              size="icon"
              onClick={undo}
              className="h-7 w-7 text-muted-foreground"
              disabled={!canUndo}
              aria-label="Отменить действие"
              title="Отменить действие (Ctrl+Z)"
            >
              <Undo2 className="h-3.5 w-3.5" />
            </Button>

            {/* Redo */}
            <Button
              variant="ghost"
              size="icon"
              onClick={redo}
              className="h-7 w-7 text-muted-foreground"
              disabled={!canRedo}
              aria-label="Повторить действие"
              title="Повторить действие (Ctrl+Shift+Z)"
            >
              <Redo2 className="h-3.5 w-3.5" />
            </Button>

            {/* Everything else in one dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground"
                  title="Дополнительные действия"
                  aria-label="Дополнительные действия"
                >
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => {
                  setCopyName(`${currentProjectName} (copy)`);
                  setSaveAsCopyOpen(true);
                }} disabled={entityCount === 0}>
                  <Copy className="h-3.5 w-3.5 mr-2" />
                  Сохранить копию…
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDslDialogOpen(true)}>
                  <Terminal className="h-3.5 w-3.5 mr-2" />
                  Импортировать модель DSL
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setAiBuilderOpen(true)}>
                  <Sparkles className="h-3.5 w-3.5 mr-2" />
                  Помощник модели
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSettingsOpen(true)}>
                  <Settings className="h-3.5 w-3.5 mr-2" />
                  Настройки
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a
                    href="/docs/using-equaflow"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <BookOpen className="h-3.5 w-3.5 mr-2" />
                    Справка EquaFlow
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a
                    href="https://github.com/megamen32/EquaFlow"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Github className="h-3.5 w-3.5 mr-2" />
                    Репозиторий GitHub
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Row 2: Scenarios + Time series (only if entities exist) */}
        {entityCount > 0 && (
          <div className="px-4 pb-1.5 flex items-center gap-3 border-t border-border/50">
            {/* Scenario tabs */}
            {scenarios.length > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-muted-foreground mr-1">Сценарии:</span>
                {scenarios.map((scenario) => (
                  <div
                    key={scenario.id}
                    className={`flex items-center rounded-full border transition-all ${getScenarioStyle(scenario.name, activeScenario === scenario.name)}`}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveScenario(scenario.name)}
                      aria-pressed={activeScenario === scenario.name}
                      title={`Показать сценарий «${scenario.name === 'Base' ? 'Рабочая гипотеза' : scenario.name}»`}
                      className="px-2 py-0.5 text-[10px]"
                    >
                      {scenario.name === 'Base' ? 'Рабочая гипотеза' : scenario.name}
                    </button>
                    {scenario.name !== 'Base' && activeScenario === scenario.name && (
                      <button
                        type="button"
                        onClick={() => removeScenario(scenario.name)}
                        className="mr-1 inline-flex hover:opacity-70"
                        aria-label={`Удалить сценарий «${scenario.name}»`}
                        title={`Удалить сценарий «${scenario.name}»`}
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setAddScenarioOpen(true)}
                  className="text-[10px] px-1.5 py-0.5 rounded-full border border-dashed border-muted-foreground/30 text-muted-foreground hover:border-foreground/50 hover:text-foreground transition-all"
                  aria-label="Добавить сценарий"
                  title="Добавить сценарий"
                >
                  <Plus className="h-2.5 w-2.5" />
                </button>
                {/* Scenario comparison toggle */}
                {scenarios.length >= 2 && (
                  <button
                    onClick={toggleComparison}
                    className={`text-[10px] px-1.5 py-0.5 rounded-full border transition-all flex items-center gap-0.5 ${
                      comparisonMode
                        ? 'bg-violet-100 text-violet-700 border-violet-300 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-700'
                        : 'border-muted-foreground/30 text-muted-foreground hover:border-foreground/50 hover:text-foreground'
                    }`}
                    title="Сравнить результаты сценариев рядом"
                  >
                    <Columns3 className="h-2.5 w-2.5" />
                    Сравнить
                  </button>
                )}
              </div>
            )}

            <div className="flex-1" />

            {/* Time series toggle */}
            <div className="flex items-center gap-1.5">
              <Button
                variant={timeEnabled ? 'default' : 'ghost'}
                size="sm"
                onClick={toggleTimeSeries}
                className={`h-6 text-[10px] ${timeEnabled ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}`}
                title={timeEnabled ? 'Выключить временные ряды' : 'Включить временные ряды'}
              >
                {timeEnabled ? `${timeHorizon} периодов` : 'Время'}
              </Button>
              {timeEnabled && (
                <Input
                  type="number"
                  value={timeHorizon}
                  onChange={(e) => setTimeHorizon(parseInt(e.target.value) || 1)}
                  className="h-6 w-14 text-[10px] text-center"
                  min={1}
                  max={60}
                />
              )}
            </div>
          </div>
        )}
      </header>

      {/* ── Canvas ── */}
      <main className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
        {workspaceView === 'visual' && (
          <ModelOverview
            projectRevision={projectRevision}
            selectedKpiFieldId={selectedKpiFieldId}
            selectKpi={selectKpi}
          />
        )}
        <div className="relative flex-1 min-h-0">
          <div className={`absolute inset-0 ${workspaceView === 'visual' ? '' : 'invisible pointer-events-none'}`} aria-hidden={workspaceView !== 'visual'}>
            <ReactFlowProvider>
              <EquaFlowCanvas />
            </ReactFlowProvider>
          </div>
          <div className={`absolute inset-0 ${workspaceView === 'spreadsheet' ? '' : 'invisible pointer-events-none'}`} aria-hidden={workspaceView !== 'spreadsheet'}>
            <SpreadsheetView />
          </div>
        </div>

        {/* Solution / Original toggle — relevant in the visual workspace. */}
        {workspaceView === 'visual' && solveStatus === 'solved' && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30">
            <button
              onClick={toggleShowSolution}
              className={`
                flex items-center gap-2.5 px-5 py-2.5 rounded-xl shadow-lg border text-sm font-semibold transition-all
                ${showSolution
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/80 dark:border-emerald-700 dark:text-emerald-300 shadow-emerald-500/20'
                  : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'
                }
              `}
              aria-label={showSolution ? 'Показать исходные значения' : 'Показать результаты расчёта'}
              title={showSolution ? 'Сейчас показаны результаты. Нажмите, чтобы увидеть исходные значения.' : 'Сейчас показаны исходные значения. Нажмите, чтобы увидеть результаты расчёта.'}
            >
              {showSolution ? (
                <>
                  <Eye className="h-4 w-4" />
                  Результаты
                </>
              ) : (
                <>
                  <EyeOff className="h-4 w-4" />
                  Исходные значения
                </>
              )}
            </button>
          </div>
        )}
      </main>

      {/* ── Footer ── */}
      <footer className="z-10 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-t border-border bg-card/60 px-4 py-1.5 text-[10px] text-muted-foreground">
        <span className="min-w-0 flex-1 truncate">
          EquaFlow — Constraint Satisfaction Engine powered by Z3
        </span>
        <div className="hidden items-center gap-3 sm:flex">
          <a
            href="/docs/using-equaflow"
            target="_blank"
            rel="noreferrer"
            className="hover:text-foreground transition-colors"
          >
            Docs
          </a>
          <a
            href="https://github.com/megamen32/EquaFlow"
            target="_blank"
            rel="noreferrer"
            className="hover:text-foreground transition-colors"
          >
            GitHub
          </a>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-slate-400" />
            Исходное
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            Результат
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-sky-400" />
            Формула
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            Рассчитано
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-400" />
            Ошибка
          </span>
          {timeEnabled && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-rose-400" />
              Период [t]
            </span>
          )}
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-teal-400" />
            График
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            Чувствительность
          </span>
          {activeScenario !== 'Base' && (
            <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
              Сценарий: {activeScenario}
            </span>
          )}
        </div>
        <button
          onClick={() => setSettingsOpen(true)}
          className="ml-auto flex items-center gap-1 transition-colors hover:text-foreground sm:ml-1"
          title="Настройки отображения и расчёта"
          aria-label="Настройки отображения и расчёта"
        >
          <Settings className="h-3.5 w-3.5" />
        </button>
      </footer>

      {/* Projects Sheet */}
      <ProjectsPanel open={projectsOpen} onOpenChange={setProjectsOpen} onOpenDSL={() => { setProjectsOpen(false); setDslDialogOpen(true); }} />

      {/* DSL Import Dialog */}
      <ImportDSLDialog isOpen={dslDialogOpen} onClose={() => setDslDialogOpen(false)} />

      {/* AI Builder Dialog */}
      <AIBuilderDialog
        isOpen={aiBuilderOpen}
        onClose={() => setAiBuilderOpen(false)}
        revision={projectRevision}
        getCurrentRevision={() => useEquaFlowStore.getState().projectRevision}
        commands={{
          previewModelPatch: (patches) => useEquaFlowStore.getState().previewModelPatch(patches),
          applyModelPatch: (revision, patches, context) => useEquaFlowStore.getState().applyModelPatch(revision, patches, context),
          createScenarioWithPatch: (revision, sourceScenarioId, name, patches, context) => useEquaFlowStore.getState().createScenarioWithPatch(revision, sourceScenarioId, name, patches, context),
          requestSolve: requestSolveAtRevision,
          requestCompare: requestCompareAtRevision,
        }}
      />

      {/* Add Scenario Dialog */}
      <Dialog open={addScenarioOpen} onOpenChange={setAddScenarioOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Scenario</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-muted-foreground">
              Add a new scenario variant. Input fields can have different values per scenario.
              If no value is set, the Base scenario value is used as fallback.
            </p>
            <Input
              value={newScenarioName}
              onChange={(e) => setNewScenarioName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddScenario()}
              placeholder="e.g. High Cost, Optimistic..."
              className="h-9"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddScenarioOpen(false)} className="h-8 text-xs">
              Cancel
            </Button>
            <Button onClick={handleAddScenario} className="h-8 text-xs" disabled={!newScenarioName.trim() || newScenarioName.trim() === 'Base'}>
              Add Scenario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save as Copy Dialog */}
      <Dialog open={saveAsCopyOpen} onOpenChange={setSaveAsCopyOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Save as Copy</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-muted-foreground">
              Create a new independent project from the current state. The current project will remain unchanged.
            </p>
            <Input
              value={copyName}
              onChange={(e) => setCopyName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveAsCopy()}
              placeholder="New project name..."
              className="h-9"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveAsCopyOpen(false)} className="h-8 text-xs">
              Cancel
            </Button>
            <Button onClick={handleSaveAsCopy} className="h-8 text-xs" disabled={!copyName.trim()}>
              Save Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Panel */}
      <SettingsPanel isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />

      <Toaster />
    </div>
  );
}
